import dayjs from 'dayjs'
import type { NewsCategory, NewsPost, NewsPriority } from '@/components/appDriver/newsfeed/types'

const categoryLabels: Record<NewsCategory, string> = {
  operations: 'ການດຳເນີນງານ',
  safety: 'ຄວາມປອດໄພ',
  promotion: 'ໂປຣໂມຊັນ',
  training: 'ການອົບຮົມ',
  system: 'ລະບົບ',
}

const categoryClasses: Record<NewsCategory, string> = {
  operations: 'bg-sky-100 text-sky-700',
  safety: 'bg-emerald-100 text-emerald-700',
  promotion: 'bg-amber-100 text-amber-700',
  training: 'bg-violet-100 text-violet-700',
  system: 'bg-slate-100 text-slate-700',
}

const priorityLabels: Record<NewsPriority, string> = {
  normal: 'ທົ່ວໄປ',
  important: 'ສຳຄັນ',
  urgent: 'ດ່ວນ',
}

const priorityClasses: Record<NewsPriority, string> = {
  normal: 'bg-gray-100 text-gray-600',
  important: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
}

export function getNewsCategoryLabel(category: NewsCategory): string {
  return categoryLabels[category]
}

export function getNewsCategoryClasses(category: NewsCategory): string {
  return categoryClasses[category]
}

export function getNewsPriorityLabel(priority: NewsPriority): string {
  return priorityLabels[priority]
}

export function getNewsPriorityClasses(priority: NewsPriority): string {
  return priorityClasses[priority]
}

export function isNewsPostExpired(post: NewsPost, now = new Date()): boolean {
  if (!post.expireAt) return false
  return new Date(post.expireAt).getTime() < now.getTime()
}

export function isNewsPostPublished(post: NewsPost, now = new Date()): boolean {
  return post.status === 'published' && new Date(post.publishAt).getTime() <= now.getTime()
}

export function filterVisibleNewsPosts(posts: NewsPost[], now = new Date()): NewsPost[] {
  return posts.filter((post) => {
    return isNewsPostPublished(post, now) &&
      !isNewsPostExpired(post, now)
  })
}

export function getNewsSortWeight(post: NewsPost): number {
  let weight = 0

  if (post.isPinned) {
    weight += 2
  }

  if (post.priority === 'urgent') {
    weight += 2
  } else if (post.priority === 'important') {
    weight += 1
  }

  return weight
}

export function sortNewsPosts(posts: NewsPost[]): NewsPost[] {
  return [...posts].sort((left, right) => {
    const weightDiff = getNewsSortWeight(right) - getNewsSortWeight(left)

    if (weightDiff !== 0) {
      return weightDiff
    }

    return new Date(right.publishAt).getTime() - new Date(left.publishAt).getTime()
  })
}

export function getLatestNewsPosts(posts: NewsPost[], limit = 2): NewsPost[] {
  return posts.slice(0, limit)
}

export function formatNewsDate(datetime: string): string {
  return dayjs(datetime).format('DD/MM/YYYY HH:mm')
}

export function getNewsContentParagraphs(content: string): string[] {
  return content
    .split('\n\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export function getNewsExcerpt(post: NewsPost, maxLength = 140): string {
  const source = post.summary?.trim() || post.content.replace(/\s+/g, ' ').trim()

  if (source.length <= maxLength) {
    return source
  }

  return `${source.slice(0, maxLength).trimEnd()}...`
}
